import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Calendar, Video, Users, ArrowRight, Radio } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const HappeningNow = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  const activities = [
    {
      id: 'agenda',
      icon: Calendar,
      title: 'Agenda Colaborativa',
      description: 'Planeje e participe de eventos da comunidade',
      color: 'primary',
      action: 'Ver Agenda'
    },
    {
      id: 'palestra',
      icon: Video,
      title: 'Palestra Online',
      description: 'Assista às palestras ao vivo com especialistas',
      color: 'accent',
      action: 'Assistir Agora',
      isLive: true
    },
    {
      id: 'mentoria',
      icon: Users,
      title: 'Mentoria',
      description: 'Sistema de aprendizado mútuo: aprenda e ensine',
      color: 'secondary',
      action: 'Participar'
    }
  ];

  return (
    <section id="happening" className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto max-w-6xl">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 mb-4">
            <Radio className="h-4 w-4 text-primary animate-pulse-glow" />
            <span className="text-sm font-medium text-primary">LIVE</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            <span className="gradient-text">Acontecendo Agora</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
            Explore as atividades em tempo real da comunidade
          </p>
        </motion.div>

        {/* Activities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {activities.map((activity, index) => {
            const Icon = activity.icon;
            return (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                onHoverStart={() => setHoveredCard(activity.id)}
                onHoverEnd={() => setHoveredCard(null)}
              >
                <Card className={`glass-strong p-6 h-full flex flex-col transition-all duration-300 ${
                  hoveredCard === activity.id ? 'glow-primary transform -translate-y-2' : ''
                }`}>
                  {/* Icon */}
                  <div className="relative mb-4">
                    <div className={`w-16 h-16 rounded-xl bg-${activity.color}/10 flex items-center justify-center animate-float`}>
                      <Icon className={`h-8 w-8 text-${activity.color}`} />
                    </div>
                    {activity.isLive && (
                      <div className="absolute -top-2 -right-2 flex items-center gap-1 bg-primary rounded-full px-2 py-1">
                        <span className="w-2 h-2 bg-primary-foreground rounded-full animate-pulse" />
                        <span className="text-xs font-bold text-primary-foreground">LIVE</span>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold mb-2">{activity.title}</h3>
                  <p className="text-muted-foreground text-sm mb-6 flex-grow">{activity.description}</p>

                  {/* Action Button */}
                  <Button 
                    variant="ghost"
                    className="w-full justify-between group hover:bg-primary/10"
                  >
                    <span>{activity.action}</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HappeningNow;